import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchAdminStats,
  fetchRecentActivity,
} from "../../store/thunks/adminThunk";
import {
  UsersIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  FireIcon,
  ChartBarSquareIcon,
  CursorArrowRaysIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../LoadingSpinner";
import { formatDistanceToNow } from "date-fns";
import { formatAddress } from "../../utils/formatAddress";

const AdminOverview = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { stats, loadingStates, errors, recentActivity } = useSelector((state) => state.admin);
  const [timeRange, setTimeRange] = useState("week");
  const [limit, setLimit] = useState(15);

  useEffect(() => {
    if (token) {
      dispatch(fetchAdminStats({ token, timeRange }));
      dispatch(fetchRecentActivity({ token, timeRange, limit }));
    }
  }, [dispatch, token, timeRange, limit]);

  if (loadingStates.stats || loadingStates.recentActivity)
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );

  if (errors.stats || errors.recentActivity)
    return <div className="text-red-600 text-center p-4">{errors.stats || errors.recentActivity}</div>;

  const statItems = [
    {
      name: "Total Users",
      value: stats?.totalUsers || 0,
      change: parseFloat(stats?.percentageChange || 0),
      icon: UsersIcon,
      subtitle: `${
        stats?.totalUsers - (stats?.totalUsersWeekAgo || 0)
      } new this week`,
    },
    {
      name: "Active Users",
      value: stats?.activeUsers || 0,
      change: parseFloat(stats?.activeUsersChange || 0),
      icon: CursorArrowRaysIcon,
      subtitle: `${
        stats?.activeUsers - (stats?.activeUsersWeekAgo || 0)
      } more than last week`,
    },
    {
      name: "Activity Logs",
      value: stats?.activityLogs || 0,
      change: parseFloat(stats?.activityLogsChange || 0),
      icon: ChartBarSquareIcon,
      subtitle: `${
        stats?.activityLogs - (stats?.activityLogsWeekAgo || 0)
      } more than last week`,
    },
    {
      name: "Property Views",
      value: stats?.propertyViews || 0,
      change: parseFloat(stats?.propertyViewsChange || 0),
      icon: EyeIcon,
      subtitle: `${
        stats?.propertyViews - (stats?.propertyViewsWeekAgo || 0)
      } more than last week`,
    },
    {
      name: "High Intent Users",
      value: stats?.highIntentUsers || 0,
      change: parseFloat(stats?.highIntentUsersChange || 0),
      icon: FireIcon,
      subtitle: `${
        stats?.highIntentUsers - (stats?.highIntentUsersWeekAgo || 0)
      } change from last week`,
    },
    {
      name: "AI Analysis",
      value: `${parseFloat(stats?.aiAnalysisCount || 0)}`,
      change: parseFloat(stats?.aiAnalysisChange || 0),
      icon: SparklesIcon,
      subtitle: `${parseFloat(
        stats?.aiAnalysisCount - (stats?.aiAnalysisWeekAgoCount || 0)
      )} more than last week`,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Overview</h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="border-gray-300 text-sm focus:ring-[#444444] focus:border-[#444444] ">
          <option value="day">Last 24 Hours</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
          <option value="year">Last Year</option>
        </select>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {statItems.map((item) => (
          <div
            key={item.name}
            className="relative overflow-hidden bg-white px-4 pb-12 pt-5 shadow sm:px-6 sm:pt-6">
            <dt>
              <div className="absolute bg-[#444444] p-3">
                <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">
                {item.name}
              </p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-6 sm:pb-7">
              <p className="text-2xl font-semibold text-gray-900">
                {typeof item.value === "number"
                  ? item.value.toLocaleString()
                  : item.value}
              </p>
              <p
                className={`ml-2 flex items-baseline text-sm font-semibold ${
                  item.change >= 0 ? "text-green-600" : "text-red-600"
                }`}>
                {item.change >= 0 ? "+" : ""}
                {item.change.toFixed(1)}%
              </p>
              <div className="absolute inset-x-0 bottom-0 bg-gray-50 px-4 py-4 sm:px-6">
                <div className="text-sm">
                  <span className="text-gray-600">{item.subtitle}</span>
                </div>
              </div>
            </dd>
          </div>
        ))}
      </div>

      {/* Activity Graph */}
      <div className="bg-white shadow">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Recent Activity
            </h3>
            <div className="flex items-center space-x-4">
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="border-gray-300 text-sm focus:ring-[#444444] focus:border-[#444444]"
              >
                <option value={15}>15 per page</option>
                <option value={30}>30 per page</option>
                <option value={45}>45 per page</option>
                <option value={60}>60 per page</option>
              </select>
            </div>
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Event Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time Spent
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    When
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentActivity?.items?.map((activity) => (
                  <tr 
                    key={activity._id}
                    className={`hover:bg-gray-50 ${activity.metadata.page !== '/dashboard' && activity.metadata.page !== '/agent' && !/^\d+$/.test(activity.metadata.page) ? 'cursor-pointer' : ''}`}
                    onClick={() => {
                      if (activity.metadata.mlsNumber && activity.metadata.boardId) {
                        window.open(`/propertydetails/${activity.metadata.mlsNumber}/${activity.metadata.boardId}`, '_blank');
                      } else {
                        window.open(activity.metadata.page, '_blank');
                      }
                    }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {activity.userDetails.firstName} {activity.userDetails.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {activity.userDetails.username}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {activity.eventType.replace(/_/g, ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {activity.address ? (
                          formatAddress(activity.address)
                        ) : activity.metadata?.page ? (
                          <span className="text-[#444444] hover:underline">
                            {activity.metadata.page}
                          </span>
                        ) : activity.eventType.includes('CAROUSEL') && activity.metadata?.carouselType ? (
                          activity.metadata.carouselType
                        ) : activity.eventType.includes('AI_NLP_CHAT_MESSAGE') && activity.metadata?.searchQuery ? (
                          activity.metadata.searchQuery
                        ) : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {activity.metadata?.timeSpent ? `${Math.round(activity.metadata.timeSpent / 1000)}s` : '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {recentActivity?.totalPages > 1 && (
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Showing page {recentActivity.currentPage} of {recentActivity.totalPages}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => dispatch(fetchRecentActivity({ token, timeRange, page: recentActivity.currentPage - 1, limit }))}
                  disabled={recentActivity.currentPage === 1}
                  className="px-3 py-1 text-sm bg-[#444444] text-white disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => dispatch(fetchRecentActivity({ token, timeRange, page: recentActivity.currentPage + 1, limit }))}
                  disabled={recentActivity.currentPage === recentActivity.totalPages}
                  className="px-3 py-1 text-sm bg-[#444444] text-white disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
